import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { updatePageSEO } from '../utils/seoUtils';

export interface LocalityZone {
  name: string;       // "Lokhandwala & Versova"
  line: string;       // "Western Line" | "Central Line" | "Harbour Line"
  station: string;    // "Andheri West Station"
  localities: string[];
  desc: string;
}

export interface AreaConfig {
  name: string;
  slug: string;
  tagline: string;
  description: string;
  localities: string[];
  zones?: LocalityZone[];
  seo: {
    title: string;
    description: string;
    keywords: string;
  };
  faqs: { q: string; a: string }[];
}

const SERVICES = [
  { icon: 'fa-mobile-alt', name: 'Screen Replacement', desc: 'Cracked or broken display fixed at your doorstep' },
  { icon: 'fa-battery-three-quarters', name: 'Battery Replacement', desc: 'Battery draining fast? Replaced on-site' },
  { icon: 'fa-plug', name: 'Charging Port Repair', desc: 'Not charging? Port fixed in under an hour' },
  { icon: 'fa-volume-up', name: 'Speaker / Mic Repair', desc: 'No sound or call issues fixed by experts' },
  { icon: 'fa-th-large', name: 'Back Glass Replacement', desc: 'Shattered back replaced with genuine parts' },
  { icon: 'fa-cog', name: 'Software Issues', desc: 'Hanging, freezing, boot loop issues resolved' },
];

const POPULAR_REPAIRS = [
  {
    brandId: 1,
    brand: 'iPhone',
    icon: 'fa-apple',
    iconBrand: true,
    repairs: [
      { label: 'Screen Replacement', keyword: 'screen' },
      { label: 'Battery Replacement', keyword: 'battery' },
      { label: 'Charging Port Repair', keyword: 'charging' },
    ],
  },
  {
    brandId: 4,
    brand: 'Samsung',
    icon: 'fa-mobile-alt',
    iconBrand: false,
    repairs: [
      { label: 'Screen Replacement', keyword: 'screen' },
      { label: 'Battery Replacement', keyword: 'battery' },
      { label: 'Charging Port Repair', keyword: 'charging' },
    ],
  },
  {
    brandId: 6,
    brand: 'OnePlus',
    icon: 'fa-mobile-alt',
    iconBrand: false,
    repairs: [
      { label: 'Screen Replacement', keyword: 'screen' },
      { label: 'Battery Replacement', keyword: 'battery' },
    ],
  },
  {
    brandId: 5,
    brand: 'Redmi',
    icon: 'fa-mobile-alt',
    iconBrand: false,
    repairs: [
      { label: 'Screen Replacement', keyword: 'screen' },
      { label: 'Battery Replacement', keyword: 'battery' },
    ],
  },
];

const TRUST = [
  { icon: 'fa-user-check', title: 'Certified Technicians', desc: 'Trained professionals with years of experience' },
  { icon: 'fa-certificate', title: 'Genuine Parts', desc: 'OEM quality parts with authenticity guarantee' },
  { icon: 'fa-shield-alt', title: '90-Day Warranty', desc: 'All repairs covered under 90-day warranty' },
  { icon: 'fa-rupee-sign', title: 'Transparent Pricing', desc: 'Price shown upfront — no hidden charges' },
];

interface Props {
  area: AreaConfig;
}

const AreaLandingPage: React.FC<Props> = ({ area }) => {
  const navigate = useNavigate();

  useEffect(() => {
    updatePageSEO({
      title: area.seo.title,
      description: area.seo.description,
      keywords: area.seo.keywords,
      canonicalUrl: `https://www.mintorepairs.com/mobile-repair-${area.slug}`,
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: `minto Mobile Repair - ${area.name}`,
        description: area.seo.description,
        url: `https://www.mintorepairs.com/mobile-repair-${area.slug}`,
        telephone: '+919324299258',
        address: {
          '@type': 'PostalAddress',
          addressLocality: area.name,
          addressRegion: 'Maharashtra',
          addressCountry: 'IN',
        },
        areaServed: area.localities.map((l) => ({ '@type': 'Place', name: l })),
        openingHours: 'Mo-Su 08:00-20:00',
        priceRange: '₹₹',
      },
    });
  }, [area]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title={`Repair in ${area.name}`} />

      {/* Hero */}
      <div className="bg-gradient-to-br from-minto-700 to-minto-500 text-white px-4 py-10">
        <div className="container mx-auto max-w-2xl text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-3">{area.tagline}</h1>
          <p className="text-sm sm:text-base text-white/90 mb-6 leading-relaxed">{area.description}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-white text-minto-600 rounded-full font-semibold text-sm hover:bg-gray-100 transition-colors shadow-md"
            >
              <i className="fas fa-tools mr-2"></i>Book Repair Now
            </button>
            <a
              href="https://wa.me/+919324299258"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-green-500 text-white rounded-full font-semibold text-sm hover:bg-green-600 transition-colors shadow-md flex items-center justify-center gap-2"
            >
              <i className="fab fa-whatsapp"></i>Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 space-y-6 max-w-3xl">

        {/* Services */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Mobile Repair Services in {area.name}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {SERVICES.map((s) => (
              <div key={s.name} className="bg-gray-50 p-3 rounded-lg">
                <i className={`fas ${s.icon} text-minto-500 mb-2 text-base block`}></i>
                <p className="text-sm font-medium text-gray-900">{s.name}</p>
                <p className="text-xs text-gray-500 mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate('/')}
            className="mt-4 w-full py-3 bg-minto-500 text-white rounded-lg text-sm font-medium hover:bg-minto-600 active:bg-minto-700 transition-colors"
          >
            Check Pricing for Your Device
          </button>
        </div>

        {/* Popular Repairs */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            Popular Repairs in {area.name}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Select your brand to check price and book doorstep repair in {area.name}.
          </p>
          <div className="space-y-4">
            {POPULAR_REPAIRS.map((item) => (
              <div key={item.brandId} className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <i className={`${item.iconBrand ? 'fab' : 'fas'} ${item.icon} text-minto-500 text-base`}></i>
                  <span className="text-sm font-semibold text-gray-900">{item.brand} Repair in {area.name}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.repairs.map((repair) => (
                    <Link
                      key={repair.keyword}
                      to={`/brand/${item.brandId}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-minto-50 text-minto-700 text-xs font-medium rounded-full border border-minto-100 hover:bg-minto-500 hover:text-white hover:border-minto-500 transition-colors"
                    >
                      <i className="fas fa-tools text-xs"></i>
                      {repair.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">How It Works</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { step: '1', icon: 'fa-mobile-alt', text: 'Select device & issue' },
              { step: '2', icon: 'fa-map-marker-alt', text: `Share your ${area.name} address` },
              { step: '3', icon: 'fa-motorcycle', text: 'Technician arrives at door' },
              { step: '4', icon: 'fa-check-circle', text: 'Repair done in ~1 hour' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="bg-minto-500 text-white w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-2">
                  {item.step}
                </div>
                <i className={`fas ${item.icon} text-minto-400 text-lg mb-1 block`}></i>
                <p className="text-xs text-gray-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Localities */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            Areas We Cover in {area.name}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Our technicians reach every corner of {area.name} and nearby localities.
          </p>
          <div className="flex flex-wrap gap-2">
            {area.localities.map((l) => (
              <span
                key={l}
                className="bg-minto-50 text-minto-700 text-xs font-medium px-3 py-1 rounded-full border border-minto-100"
              >
                {l}
              </span>
            ))}
          </div>
        </div>

        {/* Hyper-local zones */}
        {area.zones && area.zones.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              Localities We Serve in {area.name}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Our technicians cover every micro-zone in and around {area.name}.
            </p>
            <div className="space-y-4">
              {area.zones.map((zone) => (
                <div key={zone.name} className="border border-gray-100 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{zone.name}</p>
                      <p className="text-xs text-minto-600 mt-0.5">
                        <i className="fas fa-train mr-1"></i>{zone.station} · {zone.line}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">{zone.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {zone.localities.map((l) => (
                      <span key={l} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                        {l}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trust */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Why Mumbai Trusts minto</h2>
          <div className="grid grid-cols-2 gap-4">
            {TRUST.map((t) => (
              <div key={t.title} className="flex items-start gap-3">
                <div className="bg-minto-50 p-2 rounded-lg mt-0.5 shrink-0">
                  <i className={`fas ${t.icon} text-minto-500 text-sm`}></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{t.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-5 border-t border-gray-100 flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1.5">
              <i className="fas fa-star text-yellow-400"></i>
              <span className="font-semibold text-gray-900">4.8/5</span> rating
            </div>
            <div className="flex items-center gap-1.5">
              <i className="fas fa-tools text-minto-500"></i>
              <span className="font-semibold text-gray-900">5,000+</span> repairs done
            </div>
            <div className="flex items-center gap-1.5">
              <i className="fas fa-clock text-minto-500"></i>
              <span className="font-semibold text-gray-900">Same day</span> service
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            FAQs — Mobile Repair in {area.name}
          </h2>
          <div className="space-y-4">
            {area.faqs.map((faq, i) => (
              <div key={i} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                <p className="text-sm font-medium text-gray-900 mb-1">
                  <i className="fas fa-question-circle text-minto-400 mr-2"></i>
                  {faq.q}
                </p>
                <p className="text-sm text-gray-600 pl-5">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* WhatsApp CTA */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Not sure about your repair?</p>
            <p className="text-xs text-gray-600 mt-0.5">
              Chat with us free on WhatsApp — we'll diagnose it instantly
            </p>
          </div>
          <a
            href="https://wa.me/+919324299258"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-green-500 text-white text-sm rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center gap-2 shrink-0"
          >
            <i className="fab fa-whatsapp"></i>Chat
          </a>
        </div>

        {/* Bottom spacing for floating button */}
        <div className="h-16"></div>
      </div>

      <Footer />

      {/* Floating CTA */}
      <div className="fixed bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 z-50 px-4">
        <button
          onClick={() => navigate('/')}
          className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-minto-500 text-white rounded-full hover:bg-minto-600 active:bg-minto-700 transition-all duration-300 font-medium flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-lg min-w-0"
        >
          <i className="fas fa-tools text-base sm:text-lg"></i>
          <span className="whitespace-nowrap">Book Repair Now</span>
        </button>
      </div>
    </div>
  );
};

export default AreaLandingPage;
